import {
  InputLabel,
  createFilterOptions,
  AutocompleteProps,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  FormHelperText,
  Stack,
  IconButton,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';
import { forwardRef, useMemo } from 'react';

import { useComboBoxLocators } from './hooks/useComboBoxLocators';
import { StyledAutocomplete } from './styledComponents/StyledAutocomplete';
import { StyledFormControl } from './styledComponents/StyledFormControl';
import type { InputComponentWidth } from '../../types';
import { StyledChip } from './styledComponents/StyledChip';
import { StyledTextField } from './styledComponents/StyledTextField';
import { getWidth } from '../../utils/getWidth';
import Checkbox from '../Checkbox/Checkbox';
import { getDropdownItemDisplayValue, getLabelWithIcon } from '../Dropdown/helpers';
import {
  ViewModeContainer,
  ViewModeLabel,
  ViewModeValue,
} from '../Dropdown/styledComponents/ViewMode';
import type { DropdownListItem as BaseDropdownListItem } from '../Dropdown/types';
import { Tooltip } from '../Tooltip/Tooltip';
import { StyledOption } from './styledComponents/StyledOption';
import { Icon } from '../Icon/Icon';

export interface DropdownListItem extends BaseDropdownListItem {
  isActionItem?: boolean;
  key?: string;
}

export type ActionOption = {
  /**
   * The label to be shown for the action item (e.g., "+ Add new")
   */
  actionOptionLabel: string;

  /**
   * Callback to invoke when the action item is clicked
   */
  onActionOptionClick: VoidFunction;
};

export type ComboBoxProps = {
  /**
   * A unique identifier for the combo-box component.
   * Used to distinguish this combo-box from others in the application.
   */
  id: string;

  /**
   * Label of the input
   */
  label: string;

  /**
   * Whether the input is mandatory
   */
  required?: boolean;

  /**
   * Width of the input
   * Can accept predefined sizes like 'large', 'small', etc., or a custom number value.
   * @default 'medium'
   */
  width?: InputComponentWidth;

  /**
   * The placeholder text
   * @default 'Select value from list'
   */
  placeholder?: string;

  /**
   * Show the selected value in the tooltip
   * @default false
   */
  showTooltip?: boolean;

  /**
   * To display the ComboBox in the view mode
   * @default false
   * */
  viewMode?: boolean;

  /**
   * Error message to display below the field
   * When provided, the field will show error state
   */
  fieldError?: string;

  /**
   * Whether to show a checkbox for the input
   * @default false
   */
  showCheckbox?: boolean;

  /**
   * Whether to show an icon for the input
   * @default false
   */
  showIcon?: boolean;

  /**
   * Whether to display selected items at the top of the options list
   * @default false
   */
  selectedItemsOnTop?: boolean;

  /**
   * Called when a chip is clicked (for multi-select mode)
   */
  onChipClick?: (value: string) => void;

  /**
   * Called when the action button is clicked
   */
  onActionButtonClick?: (value: string) => void;

  /**
   * Optional action item to display at the top of the options list
   */
  topActionOption?: ActionOption;
} & Omit<AutocompleteProps<DropdownListItem, boolean, boolean, undefined>, 'renderInput'>;

export const ComboBox = forwardRef<HTMLInputElement, ComboBoxProps>((props, ref) => {
  const theme = useTheme();
  const {
    id,

    value: selected,
    options = [],
    disabled = false,

    label,
    required,
    width = 'medium',
    placeholder,
    showTooltip = false,
    viewMode = false,
    fieldError,
    showCheckbox = false,
    showIcon = false,
    selectedItemsOnTop = true,
    onChipClick,
    onActionButtonClick,
    topActionOption: actionItem,
    ...rest
  } = props;

  const {
    formControlNodeId,
    viewModeContainerNodeId,
    labelNodeId,
    comboBoxInputNodeId,

    listNodeId,
    listItemNodeId,

    chipNodeId,

    ariaComboBoxLabel,
  } = useComboBoxLocators({ key: id });

  const optionsWithKey = useMemo(() => {
    const items = options.map((o) => ({ ...o, key: o.value }));

    const finalItems = [...items];
    const actionItemInList = actionItem
      ? {
          value: '__action_item__',
          key: '__action_item__',
          label: actionItem?.actionOptionLabel,
          isActionItem: true,
        }
      : undefined;

    // Add action item if provided
    if (actionItemInList) {
      finalItems.unshift(actionItemInList);
    }

    if (!selectedItemsOnTop || !selected) return finalItems;

    const selectedSet = new Set(
      Array.isArray(selected) ? selected.map((item) => item.value) : [selected.value]
    );

    const [selectedItems, unselectedItems] = items.reduce(
      (acc, item) => {
        acc[selectedSet.has(item.value) ? 0 : 1].push(item);
        return acc;
      },
      [[], []] as [typeof items, typeof items]
    );

    if (selectedItems.length === 0) return finalItems;

    return [
      ...(actionItemInList ? [actionItemInList] : []),
      ...selectedItems,
      { value: 'divider', key: 'divider', divider: true } as DropdownListItem,
      ...unselectedItems,
    ];
  }, [options, selected, selectedItemsOnTop, actionItem]);

  const filterOptions = createFilterOptions<DropdownListItem>({
    matchFrom: 'any',
    ignoreCase: true,
  });

  const customLabel = label + (required ? ' *' : '');
  const viewModeValue = selected
    ? Array.isArray(selected)
      ? selected.map((v) => getDropdownItemDisplayValue(v)).join(', ')
      : getDropdownItemDisplayValue(selected)
    : undefined;

  const onTagClick = (option: DropdownListItem) => (e: React.MouseEvent<HTMLDivElement>) => {
    if (onChipClick) {
      e.stopPropagation();
      onChipClick(option.value);
    }
  };

  const onActionButtonClickHandler =
    (option: DropdownListItem) => (e: React.MouseEvent<HTMLButtonElement>) => {
      if (onActionButtonClick) {
        e.stopPropagation();
        onActionButtonClick(option.value);
      }
    };

  const renderTags = (tagValue: DropdownListItem[], getTagProps: AutocompleteRenderGetTagProps) =>
    tagValue.map((option = {} as DropdownListItem, index) => {
      const label = (
        <Typography fontSize={14} fontWeight={400} color="textPrimary">
          {getLabelWithIcon(option, { gap: 1, iconSize: 24, showIcon, iconId: id })}
        </Typography>
      );

      return (
        <StyledChip
          id={`${chipNodeId}-${getDropdownItemDisplayValue(option)}`}
          label={label}
          {...getTagProps({ index })}
          key={`combobox-chip-${option.value}`}
          status={option.status}
          withIcon={showIcon && !!option.icon}
          onClick={onTagClick(option)}
          style={onChipClick ? { cursor: 'pointer' } : undefined}
        />
      );
    });

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <Tooltip
      id={`comboBox-tooltip-${id}`}
      title={showTooltip ? viewModeValue || '' : ''}
      placement="top"
      arrow
    >
      <StyledTextField
        {...params}
        id={comboBoxInputNodeId}
        placeholder={placeholder}
        error={!!fieldError}
      />
    </Tooltip>
  );

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement> & {
      key: string;
    },
    option: DropdownListItem,
    { selected }: { selected: boolean }
  ) => {
    // Handle action item
    if (option.isActionItem && actionItem) {
      return (
        <StyledOption
          {...props}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            actionItem.onActionOptionClick?.();
          }}
          sx={{
            cursor: 'pointer',
            borderBottom: '1px solid',
            borderColor: 'divider',

            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Typography variant="subtitle1" color="primary">
            {actionItem.actionOptionLabel}
          </Typography>
        </StyledOption>
      );
    }

    const { key, onClick, ...optionProps } = props;

    if (option.divider) {
      return <Divider key={key} sx={{ my: 1 }} />;
    }

    // Custom click handler to prevent dropdown from closing when clicking on text with checkboxes
    const handleClick = (event: React.MouseEvent<HTMLLIElement>) => {
      if (showCheckbox) {
        event.preventDefault(); // Prevent default closing behavior
      }
      onClick?.(event); // Still trigger the original click handler for selection
    };

    const displayLabel = (
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        {getLabelWithIcon(option, { iconSize: 20, gap: 2, showIcon, iconId: id })}
        {option.actionButtonIcon && (
          <IconButton onClick={onActionButtonClickHandler(option)}>
            <Icon
              icon={option.actionButtonIcon}
              sx={{ fill: theme.palette.text.secondary }}
              id={`${key}-icon`}
            />
          </IconButton>
        )}
      </Stack>
    );

    return (
      <StyledOption
        id={`${listItemNodeId}-${getDropdownItemDisplayValue(option)}`}
        {...optionProps}
        key={key}
        onClick={handleClick}
        selectedItemsOnTop={selectedItemsOnTop}
      >
        {showCheckbox ? (
          <Checkbox
            id={`${key}-${getDropdownItemDisplayValue(option)}`}
            checked={selected}
            displayLabel={displayLabel}
            size="small"
            disabled={disabled}
            labelStyle={{ width: '100%' }}
            formControlLabelStyle={{ marginRight: '0px', flex: 1 }}
          />
        ) : (
          displayLabel
        )}
      </StyledOption>
    );
  };

  return (
    <StyledFormControl
      id={formControlNodeId}
      variant="outlined"
      sx={{ m: 0, width: getWidth(width) }}
      aria-labelledby={ariaComboBoxLabel}
      error={!!fieldError}
    >
      {viewMode ? (
        <ViewModeContainer id={viewModeContainerNodeId} role="presentation">
          <ViewModeLabel aria-label={ariaComboBoxLabel}>{label}</ViewModeLabel>
          <ViewModeValue aria-labelledby={ariaComboBoxLabel}>{viewModeValue || ''}</ViewModeValue>
        </ViewModeContainer>
      ) : (
        <>
          <InputLabel
            id={labelNodeId}
            htmlFor={comboBoxInputNodeId}
            sx={{ color: disabled ? 'text.disabled' : undefined }}
            aria-label={ariaComboBoxLabel}
            shrink
            children={customLabel}
          />
          <StyledAutocomplete
            autoComplete
            renderTags={renderTags}
            renderInput={renderInput}
            getOptionLabel={getDropdownItemDisplayValue}
            renderOption={renderOption}
            aria-labelledby={ariaComboBoxLabel}
            {...rest}
            ref={ref}
            disabled={disabled}
            value={selected}
            options={optionsWithKey || []}
            filterOptions={filterOptions}
            filterSelectedOptions={!showCheckbox}
            isOptionEqualToValue={(option, value: DropdownListItem) =>
              option?.value === value?.value
            }
            disableCloseOnSelect={showCheckbox}
            slotProps={{ listbox: { id: listNodeId } }}
          />
        </>
      )}

      {fieldError ? <FormHelperText error>{fieldError}</FormHelperText> : null}
    </StyledFormControl>
  );
});
